import { ERef, FirebaseTimestamp, EOrganization, ESnapshotExists } from '.';

export enum TemplateTypes {
  legal = 1,
  public = 2
}

export type TitleStyle = {
  /**
   * The font name (ex: Helvetica)
   * */
  font?: string;

  /**
   * The font size in pt.
   * */
  fontSize?: number;

  /**
   * Leading amount in pt (informs line-height).
   * */
  leading?: number;

  /**
   * Font weight
   * */
  fontWeight?: string;

  /**
   * Whether or not to underline the title.
   * */
  underline?: boolean;

  /**
   * Justification for the title.
   * */
  justification?: string;

  /**
   * Decoration for the title.
   * */
  decoration?: string;

  /**
   * Left margin for the title.
   * */
  marginLeft?: number;

  /**
   * Right margin for the title.
   * */
  marginRight?: number;

  /**
   * Space before the title paragraph in pt.
   * */
  spaceBefore?: number;

  /**
   * Space after the title paragraph in pt.
   * */
  spaceAfter?: number;
};

export type BoxModelProperties = {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
};

export type ObjectStyle = {
  /** Name of the style as defined in the IDML file */
  name: string;
  /** Raw color value from IDML */
  fillColor: string | null;
  /** Parsed/normalized version of fillColor */
  backgroundColor: string | null;
  /** Corner radius in points */
  cornerRadius: number | null;
  /** Stroke weight in points */
  strokeWeight: number | null;
  /** Raw color value from IDML */
  strokeColor: string | null;
  /** Parsed/normalized version of strokeColor */
  borderColor: string | null;
  /** Inset spacing (padding) in points */
  insetSpacing: BoxModelProperties | null;
  /** Reference to parent style if it inherits from another */
  basedOn: string | null;
};

export type ParagraphStyle = {
  /** Name of the style as defined in the IDML file */
  name: string;
  /** Font family name */
  fontFamily: string | null;
  /** Font size in points */
  fontSize: number | null;
  /** Font weight/style (e.g. Bold, Italic, Regular) */
  fontWeight: string | null;
  /** Leading (line height) in points */
  leading: number | null;
  /** Character tracking in thousandths of an em */
  tracking: number | null;
  /** Text justification (e.g. LeftAlign, CenterAlign, RightAlign) */
  justification: string | null;
  /** Whether text is underlined */
  underline: boolean | null;
  /** Left indent in points */
  leftIndent: number | null;
  /** Right indent in points */
  rightIndent: number | null;
  /** First line indent in points */
  firstLineIndent: number | null;
  /** Space before paragraph in points */
  spaceBefore: number | null;
  /** Space after paragraph in points */
  spaceAfter: number | null;
  /** Whether hyphenation is enabled */
  hyphenation: boolean | null;
  /** Raw color value from IDML */
  fillColor: string | null;
  /** Parsed/normalized version of fillColor */
  fontColor: string | null;
  /** Reference to parent style if it inherits from another */
  basedOn: string | null;
};

export type ETemplateStyles = {
  /**
   * A hash to support caching (and cache-bustig).
   */
  id: string;

  /**
   * The font name (ex: Helvetica)
   */
  font?: string;

  /**
   * The font size in pt.
   */
  fontSize?: number;

  /**
   * Leading amount in pt (informs line-height).
   */
  leading?: number;

  /**
   * Tracking amount in ??? (informs letter-spacing).
   */
  tracking?: number;

  /**
   * Indent of the first line in ???.
   */
  firstLineIndent?: number;

  /**
   * Justification for the notice body.
   */
  justification?: string;

  /**
   * PPI seting.
   * Note: this is actually *pixels* per inch, not points per inch. Points per inch is a constant (72),
   * whereas pixels per inch can vary by template.
   */
  pointsPerInch?: number;

  /**
   * Page height in px.
   */
  pageHeight?: number;

  /**
   * Page width in px at 72 PPI.
   */
  pageWidth?: number;

  /**
   * Number of columns on the page.
   */
  columnCount?: number;

  /**
   * Size of the gutter between columns (in px).
   */
  columnGutter?: number;

  /**
   *  Column positions on the page (in px).
   * Note: this is an array of numbers, representing the left and right edges of each column in order from left to right.
   */
  columnPositions?: number[];

  /**
   * Whether or not hyphenation is enabled on paragraphs
   */
  hyphenation?: boolean;

  /**
   * Size in points of the border around the notice.
   */
  borderWidth?: number;

  /**
   * Inset size in points of the border around the notice.
   */
  textInset?: number;

  /**
   * Padding around the content
   */
  padding?: BoxModelProperties;

  /**
   * Title paragraph style for the notice.
   * */
  titleParagraphStyle?: TitleStyle;

  /**
   * Styles to apply to photos in obits/classifieds
   */
  photoStyleProperties?: {
    margin?: BoxModelProperties;
  };

  /**
   * Object styles defined in the template, keyed by style name
   */
  objectStyles?: Record<string, ObjectStyle>;

  /**
   * Paragraph styles defined in the template, keyed by style name
   */
  paragraphStyles?: Record<string, ParagraphStyle>;
};

export type ETemplate = {
  isFirstPHeading?: boolean;
  downloadUrl: string;
  linerBorder?: boolean;
  type: TemplateTypes.legal | TemplateTypes.public | undefined;
  icmlSubstitutions?: Record<string, string>;
  name: string;
  fullPageTemplate?: string | null;

  /** Enable a conditional override of the two column template that is used  */
  twoColumnFullPageTemplate?: string | null;

  uploaded?: FirebaseTimestamp | null;

  /** The publisher organization that owns this template */
  organization?: ERef<EOrganization>;

  /** Other publishers that share this template */
  publisherOrganizations?: ERef<EOrganization>[];

  columnsWide: number;

  /**
   * This field was specific to Silicon Publishing and is no longer used.
   * @deprecated
   */
  sdTemplateId?: string | null;

  /**
   * Cached styles extracted from the template XML. In general this property should
   * not be accessed directly, template styles should be retrieved from the /templates/styles
   * api endpoint.
   */
  styles?: ETemplateStyles | null;

  /**
   * A way to retire an affidavit template without deleting the ref (this causes downstream problems
   * for notices already tied to that template.)
   *
   * If archived, a template will not appear on the templates table, and will not appear in dropdown
   * selections for custom templates on notice types, customers, etc.
   */
  archived?: boolean;
};

/**
 * Retreive a unique ID that changes when we update the template styles.
 * @param template template we need to retreive the ID from
 * @returns unique ID that can be used to bust caches in the ID server
 */
export const getAdTemplateCacheID = (template: ESnapshotExists<ETemplate>) => {
  const { styles } = template.data();
  return styles?.id || template.id;
};

/**
 * Default styles to apply when a template does not specify. These were
 * extracted from the "standard_noheader" template and there is nothing
 * magical about them, they can be changed freely to improve the UI/UX.
 */
export const DEFAULT_TEMPLATE_STYLES: Required<ETemplateStyles> = {
  id: 'default',
  font: 'Helvetica',
  fontSize: 8,
  leading: 9,
  tracking: 0,
  firstLineIndent: 0,
  justification: 'LeftJustified',
  pointsPerInch: 72,
  pageHeight: 1520.64,
  pageWidth: 836.64,
  columnCount: 6,
  columnPositions: [
    0, 129.42, 141.444, 270.864, 282.888, 412.308, 424.332, 553.752, 565.776,
    695.196, 707.22, 836.64
  ],
  columnGutter: 12.024,
  hyphenation: true,
  textInset: 0,
  borderWidth: 0,
  titleParagraphStyle: {},
  padding: {},
  photoStyleProperties: {},
  objectStyles: {},
  paragraphStyles: {}
};
